// src/redux/slices/authSlice.ts

import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { loginUser, loginWithOtpThunk, signupThunk, verifyOtpThunk, loginSalesUser } from "../thunks/authThunks";
import { DEFAULT_LANGUAGE_NAME, resolveLanguage } from "../../i18n/languages";

interface WarehouseDetail {
  D_Name: string;
  D_Addr1: string;
  D_City: string;
  D_State: string;
  D_Phone: string;
}

interface Route {
  Route_Number: number;
  Stop_Number: number;
}

interface SalesRep {
  S_Desc: string;
}

interface StoreDetail {
  C_CoName: string;
  C_Address: string;
  C_City: string;
  C_State: string;
  C_Phone: string;
  C_Number: string;
  C_Name: string;
  LastPaymentAmount: number;
  C_OrderDay: any;
  salesRep: SalesRep;
  Routes: Route[];
  LastBalance: number;
  C_Zip: string;
  Jurisdiction_State: string;
}

interface Customer {
  C_Number: number;
  C_Name: string;
}

interface Store {
  C_Number: number;
  C_CoName: string;
}

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  loading: boolean;
  error: string | null;
  emailPhone: string | null;
  otpSent: boolean;
  role: string | null;
  wareHouseDetail: WarehouseDetail[] | null;
  storeDetail: StoreDetail | null;
  signUpData:any;
  module: any;
  selectedCustomer: Customer | null;
  isSessionActive: any;
  logo: string | null;
  allowDiscount: boolean | null;
  discountLimit: number | null;
  allowDeliveryCharge: boolean | null;
  hasMultipleStore: boolean | null;
  stores: Store[] | null;
  selectedStore: Store | null;
  showTradeShow: boolean;
  activeTradeShows: any[] | null;
  priceBook: boolean | null;
  // Retailer-only: at least one Active, in-date pre-book is open to this customer.
  // Set from GET /retailer/pre-book/available; drives the "Pre-Book" sidebar item.
  preBook: boolean;
  // Retailer-only: whether the retailer may see their outstanding balance.
  // Comes from /auth/verify at login and is refreshed from
  // /retailer/getAllowedShowBalance. null = unknown -> treated as allowed.
  allowShowBalance: boolean | null;
  // Canonical language name (e.g. "english", "arabic"). Used by the retailer
  // language feature: drives i18n and is sent as the `language` request header
  // (only for the retailer role).
  language: string;
  // Retailer-only: false means the retailer has not seen today's advertisement
  // pop-up yet (from login); the app then fetches /retailer/getAdvertisementPopup.
  popupRead: boolean | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  token: null,
  loading: false,
  error: null,
  emailPhone: null,
  otpSent: false,
  role: null,
  wareHouseDetail: null,
  storeDetail: null,
  signUpData: null,
  module: null,
  selectedCustomer: null,
  isSessionActive: null,
  logo: null,
  allowDiscount: null,
  discountLimit: null,
  allowDeliveryCharge: null,
  hasMultipleStore: null,
  stores: null,
  selectedStore: null,
  showTradeShow: false,
  activeTradeShows: null,
  priceBook: null,
  preBook: false,
  allowShowBalance: null,
  language: DEFAULT_LANGUAGE_NAME,
  popupRead: null,
};

// Mirror the canonical language name to localStorage so i18n can pick the right
// language before React/redux-persist rehydrate, and reset it on logout.
const persistLanguage = (name: string) => {
  try {
    localStorage.setItem("language", name);
  } catch {
    /* ignore storage errors */
  }
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.emailPhone = null;
      state.otpSent = false;
      state.role = null;
      state.wareHouseDetail = null;
      state.storeDetail = null;
      state.logo= null;
      state.signUpData = null;
      state.module = null;
      state.selectedCustomer = null;
      state.isSessionActive = null;
      state.allowDiscount = null;
      state.discountLimit = null;
      state.allowDeliveryCharge = null;
      state.hasMultipleStore = null;
      state.stores = null;
      state.selectedStore = null;
      state.showTradeShow = false;
      state.activeTradeShows = null;
      state.priceBook = null;
      state.preBook = false;
      state.allowShowBalance = null;
      state.language = DEFAULT_LANGUAGE_NAME;
      state.popupRead = null;
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('emailPhone');
      localStorage.removeItem('pushNotificationDeviceId');
      localStorage.removeItem('language');
    },
    // Set the active language (canonical name, e.g. "arabic"). Used by the
    // retailer language switcher; the API call + i18n change are handled by the caller.
    setLanguage: (state, action) => {
      const name = resolveLanguage(action.payload).name;
      state.language = name;
      persistLanguage(name);
    },
    setPopupRead: (state, action: { payload: boolean }) => {
      state.popupRead = action.payload === true;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedCustomer: (state, action) => {
      state.selectedCustomer = action.payload;
    },
    updateSessionCustomer: (state, action) => {
      if (state.isSessionActive) {
        state.isSessionActive.currentCustomerId = action.payload;
      }
    },
    updateStoreDetails: (state, action) => {
      state.storeDetail = action.payload;
    },
    setLogo: (state, action) => {
      state.logo = action.payload;
    },
    // Refresh the sidebar's dynamic-tab source. Sales fetches /sales/myPermissions
    // on every app load/refresh (see Shell) so tab visibility stays in sync with
    // the latest server-side permissions, not just what login returned.
    setModule: (state, action) => {
      state.module = action.payload;
    },
    setMultipleStores: (state, action) => {
      state.hasMultipleStore = action.payload.hasMultipleStore;
      state.stores = action.payload.stores;
      if (action.payload.stores && action.payload.stores.length > 0) {
        // Always update selectedStore based on current storeDetail
        const currentStoreNumber = state.storeDetail?.C_Number;
        const currentStore = action.payload.stores.find((store: Store) => 
          store.C_Number.toString() === currentStoreNumber?.toString()
        );
        // Set selectedStore to current store if found, otherwise keep existing or set first
        state.selectedStore = currentStore || state.selectedStore || action.payload.stores[0];
      } else {
        // If no stores, clear selectedStore
        state.selectedStore = null;
      }
    },
    setSelectedStore: (state, action) => {
      state.selectedStore = action.payload;
    },
    setShowTradeShow: (state, action: { payload: boolean }) => {
      state.showTradeShow = action.payload === true;
    },
    setActiveTradeShows: (state, action) => {
      state.activeTradeShows = Array.isArray(action.payload) ? action.payload : null;
    },
    setAllowShowBalance: (state, action: { payload: boolean }) => {
      state.allowShowBalance = action.payload === true;
    },
    setPriceBook: (state, action: { payload: boolean }) => {
      // OR with existing store-derived value so an API "false" never hides
      // a Price Book that the store already enabled.
      state.priceBook = action.payload === true ? true : (state.priceBook === true ? true : false);
    },
    setPreBook: (state, action: { payload: boolean }) => {
      state.preBook = action.payload === true;
    },
    setAuthFromSwitchStore: (state, action) => {
      // Handle switchStore response like verifyOtp response
      const authData = action.payload.authData;
      const emailPhone = action.payload.emailPhone;
      state.isAuthenticated = true;
      state.token = authData.token;
      state.role = authData.role;
      state.wareHouseDetail = authData.wareHouseDetail;
      state.storeDetail = authData.storeDetail;
      state.logo = authData.logo;
      state.showTradeShow = authData.showTradeShow === true;
      state.activeTradeShows = Array.isArray(authData.activeTradeShows) ? authData.activeTradeShows : null;
      state.priceBook = authData.priceBook ?? null;
      state.allowShowBalance =
        typeof authData.allowShowBalance === "boolean" ? authData.allowShowBalance : null;
      state.language = resolveLanguage(authData.language).name;
      persistLanguage(state.language);
      state.popupRead = typeof authData.popupRead === "boolean" ? authData.popupRead : null;
      if (emailPhone) {
        state.emailPhone = emailPhone;
        localStorage.setItem('emailPhone', emailPhone);
      }
      localStorage.setItem('token', authData.token);
      localStorage.setItem('role', authData.role);
    }
  },
  extraReducers: (builder) => {
    builder
      // OTP Login
      .addCase(loginWithOtpThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      
      .addCase(loginWithOtpThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.otpSent = true;
        state.emailPhone = action.meta.arg.email_phone;
        localStorage.setItem('emailPhone', action.meta.arg.email_phone);
      })
      .addCase(loginWithOtpThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // OTP Verification
      .addCase(verifyOtpThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtpThunk.fulfilled, (state, action) => {
        const payload = action.payload?.data ?? action.payload;
        state.loading = false;
        state.isAuthenticated = true;
        state.token = payload.token;
        state.role = payload.role;
        state.wareHouseDetail = payload.wareHouseDetail;
        state.storeDetail = payload.storeDetail;
        state.otpSent = false;
        state.logo = payload.logo;
        state.showTradeShow = payload.showTradeShow === true;
        state.activeTradeShows = Array.isArray(payload.activeTradeShows) ? payload.activeTradeShows : null;
        state.priceBook = payload.priceBook === true ? true : (payload.priceBook === false ? false : null);
        state.allowShowBalance =
          typeof payload.allowShowBalance === "boolean" ? payload.allowShowBalance : null;
        state.language = resolveLanguage(payload.language).name;
        persistLanguage(state.language);
        state.popupRead = typeof payload.popupRead === "boolean" ? payload.popupRead : null;
        localStorage.setItem('token', payload.token);
        localStorage.setItem('role', payload.role);
      })
      .addCase(verifyOtpThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Signup
      .addCase(signupThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupThunk.fulfilled, (state, action: any) => {
        state.loading = false;
        state.signUpData = action?.payload?.data;
      })
      .addCase(signupThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action: any) => {
        const payload = action.payload?.data ?? action.payload;
        state.loading = false;
        state.isAuthenticated = true;
        state.token = payload.token;
        state.role = payload.role;
        state.wareHouseDetail = payload.wareHouseDetail;
        state.storeDetail = payload.storeDetail;
        state.logo = payload.logo ?? state.logo;
        state.showTradeShow = payload.showTradeShow === true;
        state.activeTradeShows = Array.isArray(payload.activeTradeShows) ? payload.activeTradeShows : null;
        state.priceBook = payload.priceBook === true ? true : (payload.priceBook === false ? false : null);
        state.allowShowBalance =
          typeof payload.allowShowBalance === "boolean" ? payload.allowShowBalance : null;
        state.language = resolveLanguage(payload.language).name;
        persistLanguage(state.language);
        state.popupRead = typeof payload.popupRead === "boolean" ? payload.popupRead : null;
        localStorage.setItem('token', payload.token);
        localStorage.setItem('role', payload.role);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Sales Login
      .addCase(loginSalesUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginSalesUser.fulfilled, (state, action: any) => {
        const data = action?.payload?.data;
        state.loading = false;
        state.isAuthenticated = true;
        state.token = data.token;
        state.role = data.role;
        state.logo = data.logo;
        state.module = data.rolesPermission;
        state.isSessionActive = data?.profile?.isSessionActive;
        state.storeDetail = data?.storeDetail;
        state.allowDiscount = data?.profile?.allowDiscount ?? null;
        state.discountLimit = data?.profile?.discountLimit ?? null;
        state.allowDeliveryCharge = data?.profile?.allowDeliveryCharge ?? null;
        state.priceBook = data?.priceBook ?? null;
        state.activeTradeShows = Array.isArray(data?.activeTradeShows) ? data.activeTradeShows : null;
        // Convert wholesaledetail object to array
        state.wareHouseDetail = data?.wholeStoreDetail ? [data.wholeStoreDetail] : null;
        // Sales is not language-aware; keep default so the retailer-only header is never sent.
        state.language = resolveLanguage(data?.language).name;
        persistLanguage(state.language);
        localStorage.setItem('token', data.token);
        localStorage.setItem('role', data.role);
      })
      .addCase(loginSalesUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
      
  },
});

export const { logout, clearError, setSelectedCustomer, updateSessionCustomer, updateStoreDetails, setLogo, setModule, setMultipleStores, setSelectedStore, setShowTradeShow, setActiveTradeShows, setPriceBook, setPreBook, setAllowShowBalance, setAuthFromSwitchStore, setLanguage, setPopupRead } = authSlice.actions;
export const selectAuth = (state: RootState) => state.auth;
export default authSlice.reducer;
